import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';
import { useJsData } from '@bingads-webui-cc-react/js-data-rest';
import { LocalNavigationRoute } from '@bingads-webui-campaign-react/ads-studio-shared-components';
import { AdsStudioPageName, isValidXandrAccount } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useNavigation } from './use-navigation';

export const getAppRoutes = ({
  // decouple with pages to make it easy to UT
  PerformancePage,
  HomePage,
  ImagesPage,
  VideosPage,
  VideoTemplatesPage,
  CreateVideoPage,
  EditVideoPage,
  BrandKitPage,
  CreateDisplayTemplatePage,
  DisplayTemplatesPage,
  EditDisplayTemplatePage,
  LibraryPage,
  WebsiteImagePanelPage,
  StockImagePanelPage,
  InvestImagePanelPage,
  InvestVideoPanelPage,
  AddVideoPanelPage,
  SearchResultPage,
  GenerateImagePage,
  UploadFilesPanelPage,
  UploadFolderPanelPage,
  MixedImagePanelPage,
  MixedVideoPanelPage,
  EditImagePage,
  AssetDetailsPage,
  BackgroundRemovalModalPage,
}) => {
  const Routes = ({
    ROOT_ROUTE,
    history,
    location,
    adFilterStore,
    updateWaterfallFilter,
    permissions,
    ...rest
  }) => {
    const { search } = location || {};

    const isXandrIntEnabled = useMemo(() => permissions.dynamic.XandrIntegrationEnabled, [permissions]);
    // eslint-disable-next-line no-restricted-globals
    const { data: account } = useJsData(jsData => (isXandrIntEnabled && rest.aid ? jsData.Account.find(rest.aid) : Promise.resolve()), [rest.aid, isXandrIntEnabled]);
    const isXandrIntegrationEnabled = useMemo(() => (isXandrIntEnabled ? isValidXandrAccount(account) : false), [account, isXandrIntEnabled]);

    // use this value as prop to pages and when it's changed it will trigger pages re-rendering
    const forceToReloadRef = useRef(null);
    const forceToReload = useCallback(() => {
      forceToReloadRef.current = {};
    }, []);

    //
    const navigation = useNavigation({
      rootRoute: ROOT_ROUTE,
      history,
      search,
      forceToReload,
      // goBack: historyStackRef.current.goBack,
      adFilterStore,
      updateWaterfallFilter,
    });

    // add additional props to pages related to navigation
    const pageProps = useMemo(() => ({
      navigation, // provide methods navigate among different pages
      reload: forceToReloadRef.current, // used to trigger pages re-rendering
      permissions,
      ...rest,
    }), [navigation, rest, permissions]);

    const renderSearchResultPage = useCallback(() => <SearchResultPage {...pageProps} search={search} />, [pageProps, search]);

    const renderPerformancePage = useCallback(() => <PerformancePage {...pageProps} />, [pageProps]);

    const renderHomePage = useCallback(() => <HomePage {...pageProps} />, [pageProps]);

    const renderImagesPage = useCallback(() => <ImagesPage {...pageProps} />, [pageProps]);

    const renderDisplayTemplatesPage = useCallback(() => <DisplayTemplatesPage {...pageProps} />, [pageProps]);

    const renderVideosPage = useCallback(() => <VideosPage {...pageProps} />, [pageProps]);

    const renderVideoTemplatesPage = useCallback(() => <VideoTemplatesPage {...pageProps} />, [pageProps]);

    const renderEditVideoPage = useCallback(() => <EditVideoPage {...pageProps} />, [pageProps]);

    const renderCreateVideoPage = useCallback(() => <CreateVideoPage {...pageProps} />, [pageProps]);

    const renderBrandKitPage = useCallback(() => <BrandKitPage {...pageProps} />, [pageProps]);

    const renderLibraryPage = useCallback(() => <LibraryPage {...pageProps} />, [pageProps]);

    const renderGenerateImagePage = useCallback(() => <GenerateImagePage {...pageProps} search={search} />, [pageProps, search]);

    const renderEditImagePage = useCallback(() => <EditImagePage {...pageProps} search={search} />, [pageProps, search]);

    const renderCreateDisplayTemplatePage = useCallback(() => <CreateDisplayTemplatePage {...pageProps} search={search} />, [pageProps, search]);

    const renderEditDisplayTemplatePage = useCallback(() => <EditDisplayTemplatePage {...pageProps} search={search} />, [pageProps, search]);

    return (
      <>
        <Switch>
          {/* performance page */}
          <Route path={`${ROOT_ROUTE}/performance`} render={renderPerformancePage} />
          {/* edit image page */}
          <Route path={`${ROOT_ROUTE}/images/:imageId/edit`} render={renderEditImagePage} />
          {/* images page */}
          <Route path={`${ROOT_ROUTE}/images`} render={renderImagesPage} />
          {/* create display template page */}
          <Route path={`${ROOT_ROUTE}/displaytemplates/create`} render={renderCreateDisplayTemplatePage} />
          {/* edit display template page */}
          <Route path={`${ROOT_ROUTE}/displaytemplates/:displayTemplateId/edit`} render={renderEditDisplayTemplatePage} />
          {/* display templates page */}
          <Route path={`${ROOT_ROUTE}/displaytemplates`} render={renderDisplayTemplatesPage} />
          {/* videos page */}
          <Route path={`${ROOT_ROUTE}/videos`} render={renderVideosPage} />
          {/* video templates page */}
          <Route path={`${ROOT_ROUTE}/videotemplates`} render={renderVideoTemplatesPage} />
          {/* create video page */}
          <Route path={`${ROOT_ROUTE}/video/create`} render={renderCreateVideoPage} />
          {/* edit video page */}
          <Route path={`${ROOT_ROUTE}/video/:videoId/edit`} render={renderEditVideoPage} />
          {/* brand kit page */}
          <Route path={`${ROOT_ROUTE}/brandkit`} render={renderBrandKitPage} />
          {/* library page */}
          <Route path={`${ROOT_ROUTE}/library`} render={renderLibraryPage} />
          {/* search result page */}
          <Route path={`${ROOT_ROUTE}/home/search`} render={renderSearchResultPage} />
          {/* render generate image page */}
          {rest.isAdsStudioAIGCMVPEnabled && !rest.isAIGCRegionRestricted && <Route path={`${ROOT_ROUTE}/generateimage`} render={renderGenerateImagePage} />}
          {/* landing page */}
          <Route path={`${ROOT_ROUTE}/home`} render={renderHomePage} />
          <Route path={`${ROOT_ROUTE}`} render={renderHomePage} />
        </Switch>
        <LocalNavigationRoute viewName={AdsStudioPageName.WebsiteImagePanelPage} render={params => <WebsiteImagePanelPage {...pageProps} {...params} />} />
        <LocalNavigationRoute viewName={AdsStudioPageName.StockImagePanelPage} render={params => <StockImagePanelPage {...pageProps} {...params} />} />
        <LocalNavigationRoute viewName={AdsStudioPageName.AddVideoPanelPage} render={params => <AddVideoPanelPage {...pageProps} {...params} />} />
        <LocalNavigationRoute viewName={AdsStudioPageName.UploadFilesPanelPage} render={params => <UploadFilesPanelPage {...pageProps} {...params} />} />
        <LocalNavigationRoute viewName={AdsStudioPageName.UploadFolderPanelPage} render={params => <UploadFolderPanelPage {...pageProps} {...params} />} />
        <LocalNavigationRoute viewName={AdsStudioPageName.MixedImagePanelPage} render={params => <MixedImagePanelPage {...pageProps} {...params} />} />
        <LocalNavigationRoute viewName={AdsStudioPageName.MixedVideoPanelPage} render={params => <MixedVideoPanelPage {...pageProps} {...params} />} />
        <LocalNavigationRoute viewName={AdsStudioPageName.AssetDetailsPage} render={params => <AssetDetailsPage {...pageProps} {...params} />} />
        <LocalNavigationRoute viewName={AdsStudioPageName.BackgroundRemovalModalPage} render={params => <BackgroundRemovalModalPage {...pageProps} {...params} />} />
        {isXandrIntegrationEnabled && <LocalNavigationRoute viewName={AdsStudioPageName.InvestImagePanelPage} render={params => <InvestImagePanelPage {...pageProps} {...params} />} />}
        {isXandrIntegrationEnabled && <LocalNavigationRoute viewName={AdsStudioPageName.InvestVideoPanelPage} render={params => <InvestVideoPanelPage {...pageProps} {...params} />} />}
      </>
    );
  };

  Routes.propTypes = {
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
      goBack: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
    }).isRequired,
    ROOT_ROUTE: PropTypes.string.isRequired,
    permissions: PropTypes.shape({
      dynamic: PropTypes.shape({}),
    }),
  };

  Routes.defaultProps = {
    permissions: { dynamic: {} },
  };

  return Routes;
};
